import { AfterViewInit, Component, inject, OnInit } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, GuardsCheckStart, ActivatedRoute, RouterOutlet } from '@angular/router';
import { useAnimation, trigger, transition, query } from '@angular/animations';
import { filter } from 'rxjs/operators';
import { AnalyticsManager } from '@fpr/core/services/analytics-manager.service';
import { fadeInAnimation } from './animations';
import { OfferManager } from '@fpr/core/services/offer-manager.service';
import { RouteData } from '@fpr/core/models/route.models';
import { SessionManager } from '@fpr/core/services/session-manager.service';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { LocaleService } from '@fpr/core/services/locale.service';
import { ConfigService } from './core/services/config.service';
import { SyndicatedHeaderService } from './core/services/syndicated-header.service';
import { AuthStatusService } from './core/services/auth-status.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    animations: [
        trigger('fadeInAnimation', [
            transition('* => *', [
                query(':self', [useAnimation(fadeInAnimation)])
            ])
        ])
    ],
    styleUrls: ['./app.component.scss'],
    imports: [TranslateModule, RouterOutlet]
})
export class AppComponent implements OnInit, AfterViewInit {

    public readonly router = inject(Router);
    public checkingGuards: boolean;

    private readonly authStatusService = inject(AuthStatusService);
    private readonly analyticsManager = inject(AnalyticsManager);
    private readonly offerManager = inject(OfferManager);
    private readonly route = inject(ActivatedRoute);
    private readonly sessionManager = inject(SessionManager);
    private readonly translate = inject(TranslateService);
    private readonly localeService = inject(LocaleService);
    private readonly configService = inject(ConfigService);
    private readonly syndicatedHeaderService = inject(SyndicatedHeaderService);

    ngOnInit() {
        this.loadTranslations();
        this.offerCheck();
        this.analyticsCheck();
        this.guardCheck();
    }

    ngAfterViewInit() {
        this.loadSyndicatedHeader();
    }

    private analyticsCheck(): void {

        this.router.events
            .pipe(
                filter((event): event is NavigationEnd => event instanceof NavigationEnd)
            )
            .subscribe(_event => {

                this.analyticsManager.resetModel();

                const routeData = this.route.firstChild.firstChild.snapshot.data as RouteData;
                const analyticsPageKey = routeData.analyticsPageKey;
                const analyticsAutoTrack = routeData.analyticsAutoTrack;

                if (analyticsPageKey) {
                    if (analyticsAutoTrack) {
                        this.analyticsManager.updateModel({}, analyticsPageKey);
                    } else {
                        this.analyticsManager.getModel(analyticsPageKey);
                    }
                }

            });

    }

    private offerCheck(): void {

        this.router.events
            .pipe(
                filter((event): event is NavigationStart => event instanceof NavigationStart)
            )
            .subscribe(event => {
                this.checkOffersetRecord(event);
            });

    }

    private guardCheck(): void {

        this.router.events
            .subscribe(event => {

                if (event instanceof GuardsCheckStart) {
                    this.checkingGuards = true;
                }

                if (event instanceof NavigationEnd) {
                    this.checkingGuards = false;
                }

            });

    }

    private checkOffersetRecord(event: NavigationStart) {

        const view = this.sessionManager.getViewNameFromRoute(event.url);

        if (!this.router.navigated) {
            this.offerManager.checkSessionOffersetRecords(view);
        } else {
            this.offerManager.checkOffersetRecords(view);
        }

    }

    private loadTranslations() {
        this.translate.setDefaultLang(this.configService.DEFAULT_LOCALE);

        this.authStatusService.getStatus().subscribe(() => {
            this.translate.use(this.localeService.locale);
        });
    }

    private loadSyndicatedHeader() {
        this.syndicatedHeaderService.initialize().catch(err => {
            console.error('[AppComponent] Failed to load syndicated header', err);
        });
    }

}
